import { Button } from "primereact/button"
import { useNavigate } from "react-router-dom"
import { useOrderSummary } from "../../../api/queries/useOrderSummary"
import { useParams } from "react-router-dom"
import { OrderParams } from "."
import { useOrderFinish } from "../../../api/queries/useOrderFinish"
import { FormEvent, useContext, useEffect, useState } from "react"
import { isOrderEditable } from "../StateTag"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import userContext from "../../../context/user"
import TermsConfirm from "../../TermsConfirm"
import { OrderFinishPayload } from "../../../api/types/order"
import styles from "./styles.module.css"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { useUserDetail } from "../../../api/queries/useUserDetail"
import { useOrderDocuments } from "../../../api/queries/useOrderDocuments"
import { OrderDocumentsModal } from "../DocumentsModal"
import { useUpdateUserTerms } from "../../../api/queries/useUpdateUserTerms"

export default function OrderFormSummary() {
  const { user } = useContext(userContext)
  const { uuid } = useParams<OrderParams>()
  const { data: orderSummary } = useOrderSummary(uuid)
  const { data: orderDetail } = useOrderDetail(uuid)
  const { data: userDetail } = useUserDetail(user?.uuid)

  const updateUserTerms = useUpdateUserTerms(user?.uuid)

  const orderDocuments = useOrderDocuments()
  const [orderDocsOrders, setOrderDocsOrders] = useState<string[] | null>(null)
  const onDocumentsModalHide = () => {
    setOrderDocsOrders(null)
    navigate("/orders")
  }

  const [isTermsConfirmed, setIsTermsConfirmed] = useState<boolean>(false)
  const handleIsTermsConfirmedChange = (event: CheckboxChangeParams) => setIsTermsConfirmed(event.checked)
  useEffect(() => {
    if (userDetail) {
      setIsTermsConfirmed(userDetail.results.userDetail.isTermsConfirmed)
    }
  }, [userDetail])

  const finishOrder = useOrderFinish(uuid)
  const navigate = useNavigate()

  const handleBackClick = () => {
    navigate(`/orders/${uuid}/calculation`)
  }

  const loading = orderSummary?.results.orderSummary?.loading
  const unloading = orderSummary?.results.orderSummary?.unloading
  const goods = orderSummary?.results.orderSummary?.goods
  const calculation = orderSummary?.results.orderSummary?.calculation
  const information = orderSummary?.results.orderSummary?.information

  const [showTermsConfirmDialog, setShowTermsConfirmDialog] = useState<boolean>(false)
  const termsConfirmCallback = (success: boolean): void => {
    if (success) {
      sendOrder(true)
    }
    setShowTermsConfirmDialog(false)
  }

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // @ts-ignore
    const isSend = event.nativeEvent.submitter.name === "send"

    if (isSend) {
      if (isTermsConfirmed) {
        await updateUserTerms.mutateAsync({ isTermsConfirmed: true })
        sendOrder(true)
      } else {
        setShowTermsConfirmDialog(true)
      }
    } else {
      // save only
      sendOrder(false)
    }
  }

  const sendOrder = async (send: boolean): Promise<void> => {
    await finishOrder.mutateAsync({ send } as OrderFinishPayload)

    if (send && uuid) {
      setOrderDocsOrders([uuid])
    } else {
      navigate("/orders")
    }
  }

  return (
    <>
      <TermsConfirm show={showTermsConfirmDialog} callback={termsConfirmCallback} />
      <OrderDocumentsModal orders={orderDocsOrders} onHide={onDocumentsModalHide} />

      <form className={"col-12"} onSubmit={handleFormSubmit}>
        <div className={"grid"}>
          {loading && (
            <div className={"col-12 md:col-6"}>
              <div className={styles.breakWord}>
                <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>Nakládka</div>

                <div className={"border-solid border-1 border-300 px-3 pt-3 mb-6"}>
                  <div className={"grid"}>
                    <div className={"field col-12"} dangerouslySetInnerHTML={{ __html: loading }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {unloading && (
            <div className={"col-12 md:col-6"}>
              <div className={styles.breakWord}>
                <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>Vykládka</div>

                <div className={"border-solid border-1 border-300 px-3 pt-3 mb-6"}>
                  <div className={"grid"}>
                    <div className={"field col-12"} dangerouslySetInnerHTML={{ __html: unloading }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {goods && (
          <div className={styles.breakWord}>
            <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>Zásilka</div>

            <div className={"border-solid border-1 border-300 px-3 pt-3 mb-6"}>
              <div className={"grid"}>
                <div className={"field col-12"} dangerouslySetInnerHTML={{ __html: goods }} />
              </div>
            </div>
          </div>
        )}

        {calculation && (
          <div className={styles.breakWord}>
            <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>Kalkulace</div>

            <div className={"border-solid border-1 border-300 px-3 pt-3 mb-6"}>
              <div className={"grid"}>
                <div className={"field col-12"} dangerouslySetInnerHTML={{ __html: calculation }} />
              </div>
            </div>
          </div>
        )}

        {information && (
          <div className={styles.breakWord}>
            <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>Informace</div>

            <div className={"border-solid border-1 border-300 px-3 pt-3 mb-6"}>
              <div className={"grid"}>
                <div className={"field col-12"} dangerouslySetInnerHTML={{ __html: information }} />
              </div>
            </div>
          </div>
        )}

        <div className={"flex justify-content-end mb-6"}>
          <table>
            <tbody>
              {orderSummary?.results.orderSummary.additionalFees.map((fee) => (
                <tr>
                  <td className="text-right">{fee.name}:</td>
                  <td className="text-right pl-3">
                    <small>{fee.value} Kč</small>
                  </td>
                </tr>
              ))}
              {orderSummary?.results.orderSummary.price && (
                <tr>
                  <td className={"text-right"}>Celková cena:</td>
                  <td className={"text-right pl-3"}><h2 className={"m-0"}>{orderSummary.results.orderSummary.price} Kč</h2></td>
                </tr>
              )}
              {orderSummary?.results.orderSummary.priceVat && (
                <tr>
                  <td className={"text-right"}>Celková cena s DPH:</td>
                  <td className={"text-right pl-3"}><h2 className={"m-0"}>{orderSummary.results.orderSummary.priceVat} Kč</h2></td>
                </tr>
              )}
              {orderSummary?.results.orderSummary.priceInfo && (
                <tr>
                  <td colSpan={2} className="text-right pt-2" style={{ color: "red" }}>
                    <strong>{orderSummary.results.orderSummary.priceInfo}</strong>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isOrderEditable(orderDetail?.results.orderDetail.state || 0) && (
          <>
            <div className={"mb-6 text-right"}>
              <Checkbox className={"mr-2"} checked={isTermsConfirmed} onChange={handleIsTermsConfirmedChange} />
              Odesláním objednávky souhlasím s <a
                href={"https://www.dopravanapaletach.cz/web/op.csp"}
                className={"text-primary"}
                target={"_blank"}
                rel={"noreferrer"}>obchodními podmínkami</a>.
            </div>

            <div className={`${styles.controls} md:flex`}>
              <div>
                <Button type={"button"} label={"Předchozí krok"}
                  className={"p-button p-button-sm p-button-secondary mr-1"}
                  icon={"pi pi-arrow-circle-left"}
                  disabled={finishOrder.isLoading} onClick={handleBackClick} />
              </div>

              <div className={"flex-1 text-right"}>
                <Button type={"submit"} label={"Uložit"} name={"save"}
                  icon={"pi pi-save"} className={"p-button p-button-sm p-button-success"}
                  disabled={finishOrder.isLoading || orderDocuments.isLoading} />

                <Button type={"submit"} label={"Uložit a odeslat"} name={"send"}
                  icon={`pi pi-${finishOrder.isLoading ? "spin pi-spinner" : "send"}`}
                  className={"p-button p-button-sm p-button-success ml-3"}
                  disabled={
                    finishOrder.isLoading ||
                    orderDocuments.isLoading ||
                    (user && !user.isActive) ||
                    !orderSummary?.results.orderSummary.isOrderValid
                  } />
              </div>
            </div>
          </>
        )}
      </form>
    </>
  )
}
